<script setup>
import { formatNumber, normalize } from "@/utils/strings"
import SectionDivider from "@/components/SectionDivider.vue"
import PointsDisplayer from "@/components/PointsDisplayer.vue"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useEventStore } from "@/stores/EventsStore"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useDisplay } from "vuetify/framework"

const { mobile } = useDisplay()

const calendarStore = useCalendarStore()
const eventStore = useEventStore()
const customerStore = useCustomerStore()

const localySelectedCoupon = ref()
const couponSelectorRef = ref()

// Handle showing and hiding the awards selector
const showAwardsSelector = ref(false)
const isLoadingLocal = ref(false)

// Get awards
const getCoupons = async() => {

  isLoadingLocal.value = true
  try {
    if (!customerStore.selectedCustomer) {

      return
    }

    if(calendarStore.availableCoupons.length === 0) {
      await calendarStore.getCoupons()
    }
    // if (!eventStore.selectedLoyaltyCoupon) {
    showAwardsSelector.value = true
    // }

  } catch (error) {
    console.warn('Error fetching customer coupons:', error)
  } finally {
    isLoadingLocal.value = false
  }
}

const affordableCoupons = computed(() =>
  calendarStore.availableCoupons.filter(
    coupon => coupon.loyaltyPointsCost <= customerStore.customerLoyaltyPoints,
  ),
)

const addCoupon = async(coupon) => {
  if (!coupon) return

  localySelectedCoupon.value = coupon
  eventStore.selectedLoyaltyCoupon = localySelectedCoupon.value

  if(eventStore.selectedLoyaltyCoupon) {
    localySelectedCoupon.value = null
  }

  couponSelectorRef.value.blur()
  // showAwardsSelector.value = false
}

const clearCoupon = async () => {
  isLoadingLocal.value = true
  if(calendarStore.availableCoupons.length === 0) {
    await calendarStore.getCoupons()
  }
  eventStore.selectedLoyaltyCoupon = null
  localySelectedCoupon.value = null
  showAwardsSelector.value = true
  isLoadingLocal.value = false
}
</script>

<template>
  <div>
    <!--          <div v-if="false">-->
    <SectionDivider
      v-if="calendarStore.loyaltyProgramSettings.enabled || eventStore.selectedLoyaltyCoupon"
      :text="$t('loyaltyProgram')"
      :class="mobile ? 'my-6' : 'my-4'"
    />

    <PointsDisplayer
      v-if="calendarStore.loyaltyProgramSettings.enabled || eventStore.loyaltyPointsEarned || eventStore.selectedEvent?.loyaltyProgramPoints?.gained"
      class="my-2"
      :points="(!eventStore.allowSave
        ? eventStore.selectedEvent?.loyaltyProgramPoints?.gained
        : eventStore.loyaltyPointsEarned) ?? 0"
      :text="$t('points_accrued')"
    />
    <div
      v-if="calendarStore.loyaltyProgramSettings.enabled && customerStore.selectedCustomer"
      class="d-flex ga-4 align-center flex-wrap mt-2"
    >
      <PointsDisplayer
        :points="customerStore.customerLoyaltyPoints"
        :text="$t('points_held')"
      />

      <!--                v-if="eventStore.selectedLoyaltyCoupon !== 0 || showAwardsSelector === true"-->
      <VBtn
        v-if="!showAwardsSelector && !eventStore.selectedLoyaltyCoupon && customerStore.customerLoyaltyPoints > 0"
        id="showSendSmsToCustomerBtn"
        class="flex-md-fill"
        color="secondary"
        :loading="isLoadingLocal"
        :disabled="!customerStore.selectedCustomer || eventStore.isSaving || !eventStore.allowSave"
        size="small"
        variant="flat"
        @click="getCoupons"
      >
        <span class="xs-font-size">{{ $t('redeem') }}</span>
      </VBtn>
    </div>
    <VSlideYTransition>
      <div
        v-if="eventStore.selectedCustomer"
        class="my-2"
      >
        <VAutocomplete
          v-if="showAwardsSelector && calendarStore.loyaltyProgramSettings.enabled && !eventStore.selectedLoyaltyCoupon"
          ref="couponSelectorRef"
          v-model="localySelectedCoupon"
          density="compact"
          class="mt-2"
          :loading="isLoadingLocal"
          :items="affordableCoupons"
          :item-title="item => item.name"
          :item-value="item => item.id"
          :placeholder="$t('select')"
          :disabled="!customerStore.selectedCustomer || eventStore.isSaving || !eventStore.allowSave"
          :label="$t('chooseAnAwards')"
          auto-select-first
          return-object
          color="secondary"
          variant="outlined"
          :custom-filter="(value, query) => normalize(value).includes(normalize(query))"
          :no-data-text="$t('noAvailableAwards')"
          @update:model-value="addCoupon($event)"
        >
          <template #item="{ item, props }">
            <VListItem
              class="award-item"
              v-bind="props"
            >
              <VListItemSubtitle>
                <span
                  v-if="item.raw?.value && item.raw?.value !== 0"
                  class="mr-2"
                >
                  {{ `${formatNumber(item.raw?.value)} ` }}
                  {{ item.raw?.discountType === 'PERCENT' ? '%' : calendarStore.currency }}

                </span>
                <span>
                  {{ `(-${item.raw?.loyaltyPointsCost} ${$t('pts')}.)` }}
                </span>
              </VListItemSubtitle>
            </VListItem>
          </template>
        </VAutocomplete>
      </div>
    </VSlideYTransition>
  </div>
  <div v-if="eventStore.selectedLoyaltyCoupon">
    {{ $t('selectedAward') }}:
    <VListItem
      class="mt-1 border-thin rounded-lg"
      :disabled="!eventStore.allowSave"
    >
      <!-- {{ customerStore.loyaltyProgramCoupon }} -->
      <VListItemTitle class="white-space-break line-height-normal">
        {{ eventStore.selectedLoyaltyCoupon.name }}
      </VListItemTitle>
      <VListItemSubtitle>
        <span class="mr-2">
          {{ formatNumber(eventStore.selectedLoyaltyCoupon.value) }}
          {{ eventStore.selectedLoyaltyCoupon?.discountType === 'PERCENT' ? '%' : calendarStore.currency }}
        </span>
        <span>{{ `(-${eventStore.selectedLoyaltyCoupon?.loyaltyPointsCost} ${$t('pts')}.)` }}</span>
      </VListItemSubtitle>

      <template #append>
        <span v-if="eventStore.allowSave">
          <VBtn
            icon="ri-close-line"
            variant="plain"
            :ripple="false"
            :loading="isLoadingLocal"
            @click="clearCoupon"
          />
          <VTooltip activator="parent">
            {{ $t('removeCoupon') }}
          </VTooltip>
        </span>
      </template>
    </VListItem>
  </div>
</template>


